/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/ban-types */

import React, { useEffect, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";

import $ from "jquery";

import "datatables.net";
import "datatables.net-buttons";
import "datatables.net-buttons/js/buttons.print.js";
import "datatables.net-buttons/js/buttons.html5.js";
import "datatables.net-buttons/js/buttons.colVis.js";
import "datatables.net-buttons/js/buttons.flash.js";
import "datatables.net-dt";
import "datatables.net-dt/css/jquery.dataTables.min.css";
import "datatables.net-responsive-dt";
import "datatables.net-responsive-dt/css/responsive.dataTables.min.css";

import {
  SpeedDial,
  SpeedDialMenu
} from "@npm-telluria-tecnologia/telluria-ui/dist";

import {
  Search,
  ArrowForwardIos,
  ArrowBackIos,
  KeyboardDoubleArrowRight,
  KeyboardDoubleArrowLeft,
  Add
} from "@libraries/icons";

import useTranslation from "@languages/useTranslation";
import { DataTableMessages } from "@languages/interfaces";

import useTheme from "@styles/useTheme";
import * as Styled from "./styles";

export interface DataTableActions<T = any> {
  ref: string;
  callback: (rowData: T) => any;
}
export type DataTableButtons = DataTables.ButtonSettings & {
  key: string;
  name: string;
  callback?: () => any;
};

type DataTableProps = {
  data: any[];
  columns: DataTables.ColumnSettings[];
  settings?: DataTables.Settings;
  actions?: DataTableActions[];
  buttons?: DataTableButtons[];
  returnTable?: (table: DataTables.Api<any>) => void;
  returnClickedRow?: (clickedRow: JQuery<HTMLTableRowElement>) => void;
};
const DataTable: React.FC<DataTableProps> = ({
  data,
  columns,
  settings,
  actions,
  buttons,
  returnTable,
  returnClickedRow
}) => {
  const tableRef = useRef<any>(null);

  const { theme } = useTheme();
  const { t, i18n } = useTranslation();

  const [table, setTable] = useState<DataTables.Api<any>>();
  const [openSpeedDialActionMenus, setOpenSpeedDialActionMenus] = useState(false);

  /** Speed dial action menus handlers */
  const handleOpenActionMenusSpeedDial = () => {
    setOpenSpeedDialActionMenus(true);
  };
  const handleCloseActionMenusSpeedDial = () => {
    setOpenSpeedDialActionMenus(false);
  };

  // Render the Data Table
  // PS: If language changes, the table will be reloaded
  useEffect(() => {
    const initialTableSettings: DataTables.Settings = {};

    if (table) initialTableSettings.destroy = true;

    // REVIEW need to redraw component (reorder button, input, etc.) after change resolution
    // Only render one time when screen has platform
    if (!table || initialTableSettings.destroy) {
      const tableSettings: DataTables.Settings = {
        ...initialTableSettings,
        responsive: true,
        data,
        columns,
        language: {
          paginate: {
            first: ReactDOMServer.renderToString(<KeyboardDoubleArrowLeft />),
            last: ReactDOMServer.renderToString(<KeyboardDoubleArrowRight />),
            next: ReactDOMServer.renderToString(<ArrowForwardIos />),
            previous: ReactDOMServer.renderToString(<ArrowBackIos />)
          },
          emptyTable: t(DataTableMessages.dtEmptyTable),
          info: t(DataTableMessages.dtInfo),
          infoEmpty: t(DataTableMessages.dtInfoEmpty),
          infoFiltered: `<br>${t(DataTableMessages.dtInfoFiltered)}`,
          lengthMenu: t(DataTableMessages.dtLengthMenu),
          loadingRecords: t(DataTableMessages.dtLoadingRecords),
          processing: t(DataTableMessages.dtProcessing),
          zeroRecords: t(DataTableMessages.dtZeroRecords),
          search: ReactDOMServer.renderToString(<Search />),
          searchPlaceholder: t(DataTableMessages.dtSearchPlaceholder)
        },
        scrollY: "calc(100vh - 400px)",
        scrollX: false,
        scrollCollapse: true,
        autoWidth: true,
        dom: (() => {
          const gridHeader = "MuiGrid-root MuiGrid-item MuiGrid-grid-xs-6";
          const gridFooter = "MuiGrid-root MuiGrid-item MuiGrid-grid-xs-4";
          const container = "MuiGrid-root MuiGrid-container";

          return `
            <"${container} dataTables_search"
              <"${gridHeader}"f>
            >
              rt
            <"${container} fixed--bottom"
              <"${gridFooter}"l>
              <"${gridFooter}"i>
              <"${gridFooter}"p>
              <"${gridFooter} flex--center flex--hidden"B>
            >`;
        })(),
        buttons: buttons ?? []
      };

      const instanceTable = $(tableRef.current).DataTable({
        ...tableSettings,
        ...settings
      });

      setTable(instanceTable);
      returnTable && returnTable(instanceTable);
    }
  }, [i18n.language, table, returnTable, settings, buttons, data, columns, t]);

  // Update data in Data Table when data is change
  useEffect(() => {
    const ref = tableRef.current;

    if (table) {
      table?.clear();
      table.rows.add(data);
      table.draw();
      table?.columns.adjust();

      if (actions && data) {
        returnTable && returnTable(table);

        actions.forEach((action) => {
          $(tableRef.current).on("click", `${action.ref}`, (event) => {
            if ($(event.currentTarget).parents("tr").hasClass("child")) {
              action.callback(
                table
                  .row($(event.currentTarget).parents("tr").prev("tr"))
                  .data()
              );
              returnClickedRow
                && returnClickedRow(
                  $(event.currentTarget).parents("tr").prev("tr")
                );
            } else {
              action.callback(
                table.row($(event.currentTarget).parents("tr")).data()
              );
              returnClickedRow
                && returnClickedRow($(event.currentTarget).parents("tr"));
            }
          });
        });
      }
    }

    // REVIEW The code behaves strangely, needing to call the function twice to adjust the size of the columns
    table?.columns.adjust();

    return function cleanUp() {
      $(ref).prop("onclick", null).off("click");
    };
  }, [data, table, actions, returnTable, returnClickedRow]);

  return (
    <Styled.Container>
      <table
        className="display cell-border hover row-border compact"
        width="100%"
        ref={tableRef}
      />
      {buttons && buttons.length > 0 && (
        <Styled.Buttons>
          <SpeedDial
            primaryColor={theme.colors.primary}
            secondColor={theme.colors.secondary}
            size="small"
            icon={<Add fontSize="large" />}
            open={openSpeedDialActionMenus}
            onOpen={handleOpenActionMenusSpeedDial}
            onClose={handleCloseActionMenusSpeedDial}
          >
            {buttons.map((menu) => (
              <SpeedDialMenu
                key={menu.key}
                menu={{ id: menu.key, title: menu.name }}
                colorOnHover={theme.colors.secondary}
                onClick={() => {
                  menu.callback && menu.callback();
                }}
              />
            ))}
          </SpeedDial>
        </Styled.Buttons>
      )}
    </Styled.Container>
  );
};

DataTable.defaultProps = {
  settings: {},
  actions: [],
  buttons: undefined,
  returnTable: undefined,
  returnClickedRow: undefined
};

export default DataTable;
